import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, Eye, EyeOff, AlertCircle, Loader2, Check, Wallet, TrendingDown, TrendingUp, Percent } from 'lucide-react';
import { EMAIL_CONFIG } from '../config/constants';
import Logo from '../logo.svg';
import AppPromoModal from '../components/AppPromoModal';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { signInWithEmail, signInWithGoogle, loading, error } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [showPromoModal, setShowPromoModal] = useState(false);

  useEffect(() => {
    const hasSeenModal = localStorage.getItem('suivifinance_promo_modal_dismissed');
    if (!hasSeenModal) {
      const timer = setTimeout(() => setShowPromoModal(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClosePromoModal = () => {
    setShowPromoModal(false);
    localStorage.setItem('suivifinance_promo_modal_dismissed', 'true');
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (!email || !password) {
      setLocalError('Veuillez remplir tous les champs');
      return;
    }
    try {
      const result = await signInWithEmail(email, password);
      if (result && 'requiresVerification' in result && result.requiresVerification) {
        sessionStorage.setItem('pending_login_email', email);
        sessionStorage.setItem('pending_login_password', password);
        navigate('/verify-login-code', { state: { email } });
        return;
      }
      navigate('/');
    } catch (error: any) {
      setLocalError(error.message || 'Erreur lors de la connexion');
    }
  };

  const handleGoogleLogin = async () => {
    setLocalError(null);
    try {
      await signInWithGoogle();
    } catch (error: any) {
      setLocalError(error.message || 'Erreur lors de la connexion Google');
    }
  };

  const displayError = localError || error;

  return (
    <>
      <AppPromoModal isOpen={showPromoModal} onClose={handleClosePromoModal} />
      <div className="min-h-screen bg-gradient-to-br from-dark-900 via-dark-800 to-dark-900 flex items-center justify-center p-4 lg:p-8 animate-login-fade-in">
        <div className="w-full max-w-[1200px] min-h-[calc(100vh-4rem)] lg:min-h-0 flex flex-col lg:grid lg:grid-cols-2 lg:gap-12 xl:gap-16 lg:items-center">
          {/* ——— Mobile : formulaire en premier ——— */}
          {/* ——— Desktop : colonne gauche 50% ——— */}
          <div className="order-2 lg:order-1 flex flex-col justify-center lg:py-8">
            {/* Hero condensé sur mobile */}
            <div className="lg:px-0">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-[2.5rem] font-bold text-white leading-tight tracking-tight">
                Pilotez vos finances avec clarté.
              </h1>
              <p className="mt-4 lg:mt-6 text-base lg:text-lg text-dark-400 max-w-lg" style={{ opacity: 0.9 }}>
                Tableaux de bord, catégories, transactions périodiques et dépenses partagées — tout au même endroit.
              </p>

              <ul className="mt-6 lg:mt-8 space-y-4" role="list">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary-500/15 flex items-center justify-center mt-0.5" aria-hidden>
                    <Check className="w-3 h-3 text-primary-500" strokeWidth={2.5} />
                  </span>
                  <span className="text-dark-300 text-sm lg:text-base">Vue instantanée de vos dépenses et revenus</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary-500/15 flex items-center justify-center mt-0.5" aria-hidden>
                    <Check className="w-3 h-3 text-primary-500" strokeWidth={2.5} />
                  </span>
                  <span className="text-dark-300 text-sm lg:text-base">Graphiques mensuels et annuels</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary-500/15 flex items-center justify-center mt-0.5" aria-hidden>
                    <Check className="w-3 h-3 text-primary-500" strokeWidth={2.5} />
                  </span>
                  <span className="text-dark-300 text-sm lg:text-base">Dépenses partagées : couple, coloc, projets</span>
                </li>
              </ul>

              {/* Card preview premium */}
              <div
                className="mt-8 lg:mt-10 p-6 rounded-xl shadow-lg border bg-dark-800/80"
                style={{ borderColor: 'rgba(255,255,255,0.08)' }}
                role="presentation"
                aria-hidden
              >
                <p className="text-xs font-medium text-dark-500 uppercase tracking-widest mb-4">
                  Aperçu du tableau de bord
                </p>
                <div className="grid grid-cols-2 gap-4 lg:gap-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                      <Wallet className="w-5 h-5 text-primary-500" />
                    </div>
                    <div>
                      <p className="text-xs text-dark-500">Solde du mois</p>
                      <p className="text-sm font-semibold text-white">2 450 €</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-dark-600/80 flex items-center justify-center flex-shrink-0">
                      <TrendingDown className="w-5 h-5 text-dark-400" />
                    </div>
                    <div>
                      <p className="text-xs text-dark-500">Dépenses</p>
                      <p className="text-sm font-semibold text-white">1 820 €</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="w-5 h-5 text-primary-500" />
                    </div>
                    <div>
                      <p className="text-xs text-dark-500">Revenus</p>
                      <p className="text-sm font-semibold text-white">4 270 €</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                      <Percent className="w-5 h-5 text-primary-500" />
                    </div>
                    <div>
                      <p className="text-xs text-dark-500">Taux d'épargne</p>
                      <p className="text-sm font-semibold text-white">57 %</p>
                    </div>
                  </div>
                </div>
              </div>

              <p className="mt-6 lg:mt-8 text-xs lg:text-sm text-dark-500">
                🔒 Vos données sont protégées. Aucun accès à votre banque.
              </p>
            </div>
          </div>

          {/* ——— Colonne droite 50% : formulaire ——— */}
          <div className="order-1 lg:order-2 flex flex-col justify-center items-center lg:py-8">
            <div className="w-full max-w-[420px]">
              <div className="rounded-2xl border bg-dark-800/90 shadow-xl p-8 border-dark-700/80">
                <div className="mb-6">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white/5 border border-white/[0.08] mb-4">
                    <img src={Logo} alt="" className="w-7 h-7" />
                  </div>
                  <h2 className="text-xl font-semibold text-white">Connexion</h2>
                  <p className="text-sm text-dark-500 mt-1">Accédez à votre espace en quelques secondes</p>
                </div>

                {displayError && (
                  <div
                    id="login-error"
                    className="mb-6 p-3 rounded-lg border border-red-500/20 bg-red-500/10 flex items-center gap-2"
                    role="alert"
                  >
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" aria-hidden />
                    <span className="text-red-400 text-sm">{displayError}</span>
                  </div>
                )}

                <div>
                  <button
                    type="button"
                    onClick={handleGoogleLogin}
                    disabled={loading}
                    className="w-full h-12 px-4 rounded-xl bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-800 font-medium flex items-center justify-center gap-3 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-2 focus:ring-offset-dark-800"
                    aria-label="Continuer avec Google"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" aria-hidden />
                    ) : (
                      <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden>
                        <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                        <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                        <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                        <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                      </svg>
                    )}
                    <span>{loading ? 'Connexion...' : 'Continuer avec Google'}</span>
                  </button>
                  <p className="text-center text-xs text-dark-500 mt-2">Connexion en 1 clic</p>
                </div>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/[0.06]" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="px-3 bg-dark-800 text-dark-500 text-xs uppercase tracking-wider">ou</span>
                  </div>
                </div>

                <form onSubmit={handleEmailLogin} className="space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-dark-400 mb-2">
                      Adresse email
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-dark-500">
                        <Mail className="h-5 w-5" aria-hidden />
                      </div>
                      <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full h-12 pl-10 pr-4 py-3 rounded-xl bg-dark-800 border border-white/[0.08] text-white placeholder-dark-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-transparent transition-shadow"
                        placeholder={EMAIL_CONFIG.PLACEHOLDER}
                        disabled={loading}
                        autoComplete="email"
                        aria-invalid={!!displayError}
                        aria-describedby={displayError ? 'login-error' : undefined}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-dark-400 mb-2">
                      Mot de passe
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-dark-500">
                        <Lock className="h-5 w-5" aria-hidden />
                      </div>
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full h-12 pl-10 pr-12 py-3 rounded-xl bg-dark-800 border border-white/[0.08] text-white placeholder-dark-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-transparent transition-shadow"
                        placeholder="Votre mot de passe"
                        disabled={loading}
                        autoComplete="current-password"
                        aria-invalid={!!displayError}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-dark-500 hover:text-dark-300 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-inset rounded-r-xl"
                        disabled={loading}
                        aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                        tabIndex={0}
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" aria-hidden /> : <Eye className="h-5 w-5" aria-hidden />}
                      </button>
                    </div>
                  </div>

                  <div className="text-right">
                    <Link
                      to="/forgot-password"
                      className="text-xs text-dark-500 hover:text-primary-500 transition-colors focus:outline-none focus:underline"
                    >
                      Mot de passe oublié ?
                    </Link>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 min-h-[48px] px-4 rounded-xl bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white font-medium flex items-center justify-center gap-2 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-2 focus:ring-offset-dark-800 shadow-lg shadow-primary-500/10"
                    aria-busy={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" aria-hidden />
                        <span>Connexion...</span>
                      </>
                    ) : (
                      <span>Accéder à mon tableau de bord</span>
                    )}
                  </button>
                </form>

                <div className="mt-6 pt-6 border-t border-white/[0.06] text-center">
                  <p className="text-sm text-dark-500">Nouveau sur SuiviFinance ?</p>
                  <Link
                    to="/register"
                    className="inline-block mt-2 text-sm font-medium text-primary-500 hover:text-primary-400 transition-colors focus:outline-none focus:underline"
                  >
                    Créer un compte gratuitement (30 sec)
                  </Link>
                </div>
              </div>

              <p className="mt-6 text-center text-xs text-dark-500">
                En vous connectant, vous acceptez nos{' '}
                <Link to="/terms" className="text-dark-400 hover:text-primary-500 focus:outline-none focus:underline transition-colors">
                  conditions d'utilisation
                </Link>{' '}
                et notre{' '}
                <Link to="/privacy" className="text-dark-400 hover:text-primary-500 focus:outline-none focus:underline transition-colors">
                  politique de confidentialité
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
